/**
 * @file src/boot/bootTrace.js
 * Opt-in boot timing trace — `?bootTrace=1`.
 *
 * A realm's load is many overlapping timelines (one per voice) plus the room plumbing that feeds
 * them, and the only question that matters when it feels slow is WHICH of those is the long pole.
 * Reading that off a screen full of spinners is guesswork, so this prints it: every mark carries a
 * millisecond offset from ONE realm-wide epoch, so lines from different voices are directly
 * comparable and a serialization shows up as a staircase rather than having to be inferred.
 *
 * Off by default and costing nothing when off (`isBootTraceEnabled` is resolved once from the URL,
 * and every mark returns before it formats anything). It is a diagnostic, never a dependency: no
 * behaviour reads a mark, so a session with the flag on takes exactly the same path as one without.
 */

const EPOCH = typeof performance !== 'undefined' && typeof performance.now === 'function'
  ? performance.now()
  : Date.now();

function readFlag() {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return false;
  try {
    const value = (new URLSearchParams(window.location.search).get('bootTrace') || '')
      .trim()
      .toLowerCase();
    return value === '1' || value === 'true';
  } catch (_) {
    return false;
  }
}

// Resolved ONCE. The flag cannot change mid-session, and re-parsing the query string on every mark
// would put string work on paths that fire per download chunk.
const ENABLED = readFlag();

/** True when this session was booted with `?bootTrace=1`. */
export function isBootTraceEnabled() {
  return ENABLED;
}

function now() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** Milliseconds since the realm epoch (module load), the x-axis every mark is plotted on. */
export function bootTraceElapsed() {
  return now() - EPOCH;
}

/**
 * Record one point on the boot timeline.
 * @param {string} scope who is reporting — a voiceId, `deck:<n>`, or `realm`.
 * @param {string} label what happened, in the imperative past ('release warmed', 'voice built').
 * @param {object} [detail] extra fields; printed as-is, so keep it to ids and counts.
 */
export function bootTrace(scope, label, detail = null) {
  if (!ENABLED) return;
  const ms = bootTraceElapsed();
  const at = ms.toFixed(0).padStart(6, ' ');
  if (detail) console.log(`[bootTrace] +${at}ms  ${scope}  ${label}`, detail);
  else console.log(`[bootTrace] +${at}ms  ${scope}  ${label}`);
  // Also kept in an array on `window`, because the console is where a trace is READ and an array is
  // where it can be sorted, diffed against a second run, or pasted into a ticket. Only while the flag
  // is on, so nothing accumulates in an ordinary session.
  if (typeof window !== 'undefined') {
    (window.__bootTrace ||= []).push({ at: Math.round(ms), scope, label, ...(detail ?? {}) });
  }
}

/**
 * Time one awaited step and mark it with its own duration — the shape that answers "how long did
 * THIS take" rather than only "when did it end". Returns the awaited value untouched, and marks a
 * rejection too before rethrowing: a step that fails slowly is exactly as interesting as one that
 * succeeds slowly, and losing it would leave a gap in the timeline with no explanation in it.
 */
export async function bootTraceStep(scope, label, run) {
  if (!ENABLED) return run();
  const started = now();
  bootTrace(scope, `${label} →`);
  try {
    const result = await run();
    bootTrace(scope, `${label} ✓`, { tookMs: Math.round(now() - started) });
    return result;
  } catch (error) {
    bootTrace(scope, `${label} ✗`, { tookMs: Math.round(now() - started) });
    throw error;
  }
}
